import React, { useCallback, useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload, faEnvelope } from "@fortawesome/free-solid-svg-icons";
import { Modal, Spinner, ErrorBanner } from "../../../components/ui";
import { raffleService } from "../../../services/api";
import styles from "../profile/components/history/TicketModal.module.css";

function receiptFileName(code) {
  const safe = String(code || "gold")
    .replace(/[^A-Za-z0-9_-]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return `comprobante-rifa-${safe || "gold"}.png`;
}

/**
 * Comprobante (PNG) de una compra de boletas. Se carga desde
 * GET /raffles/receipts/{code}/image/ con auth y se muestra con un object URL
 * que se revoca al cerrar. El correo solo sale si el usuario pulsa "Enviarme por correo".
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {string|null} props.receiptCode
 * @param {string} [props.raffleTitle]
 * @param {() => void} props.onClose
 */
export default function RaffleReceiptModal({ isOpen, receiptCode, raffleTitle, onClose }) {
  const [status, setStatus] = useState("idle"); // idle | loading | ready | error
  const [imageUrl, setImageUrl] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null); // { ok, message }
  const urlRef = useRef(null);

  const revokeUrl = useCallback(() => {
    if (urlRef.current) {
      URL.revokeObjectURL(urlRef.current);
      urlRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!isOpen || !receiptCode) return undefined;
    let cancelled = false;

    setStatus("loading");
    setLoadError("");
    setImageUrl(null);
    revokeUrl();

    raffleService.getReceiptImage(receiptCode).then((res) => {
      if (cancelled) return;
      if (res.ok && res.blob) {
        const url = URL.createObjectURL(res.blob);
        urlRef.current = url;
        setImageUrl(url);
        setStatus("ready");
      } else {
        setLoadError(res.error || "No pudimos cargar el comprobante.");
        setStatus("error");
      }
    });

    return () => {
      cancelled = true;
      revokeUrl();
    };
  }, [isOpen, receiptCode, reloadKey, revokeUrl]);

  useEffect(() => {
    if (!isOpen) {
      setSendResult(null);
      setSending(false);
      setImageUrl(null);
      setStatus("idle");
    }
  }, [isOpen]);

  useEffect(() => {
    setSendResult(null);
  }, [receiptCode]);

  const handleDownload = () => {
    if (!imageUrl) return;
    const link = document.createElement("a");
    link.href = imageUrl;
    link.download = receiptFileName(receiptCode);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const handleSendEmail = async () => {
    if (!receiptCode || sending) return;
    setSending(true);
    setSendResult(null);
    const res = await raffleService.sendReceiptEmail(receiptCode);
    setSendResult({ ok: res.ok, message: res.message });
    setSending(false);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Tu comprobante" size="md">
      <div className={styles.content}>
        {status === "loading" && (
          <div className={styles.loading}>
            <Spinner label="Cargando comprobante…" />
          </div>
        )}

        {status === "error" && (
          <ErrorBanner message={loadError} onRetry={() => setReloadKey((k) => k + 1)} />
        )}

        {status === "ready" && imageUrl && (
          <>
            <div className={styles.ticketFrame}>
              <img
                src={imageUrl}
                alt={`Comprobante ${receiptCode}${raffleTitle ? ` de ${raffleTitle}` : ""}`}
                className={styles.ticketImg}
              />
            </div>

            <div className={styles.actions}>
              <button type="button" className={styles.primaryBtn} onClick={handleDownload}>
                <FontAwesomeIcon icon={faDownload} />
                Descargar
              </button>
              <button
                type="button"
                className={styles.secondaryBtn}
                onClick={handleSendEmail}
                disabled={sending}
                aria-busy={sending}
              >
                <FontAwesomeIcon icon={faEnvelope} />
                {sending ? "Enviando…" : "Enviarme por correo"}
              </button>
            </div>

            {sendResult && (
              <p
                className={`${styles.sendMsg} ${sendResult.ok ? styles.sendOk : styles.sendError}`}
                role={sendResult.ok ? "status" : "alert"}
              >
                {sendResult.message}
              </p>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}
